import { useRef, useState, useLayoutEffect, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useI18n } from '../../context/I18nContext';
import useClickOutside from '../../hooks/useClickOutside';
import MentionSuggestionItem from './MentionSuggestionItem';
import './MentionAutocomplete.css';

/**
 * Autocomplete popup component for @mentions
 * Displays a list of room member suggestions and handles selection
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether the popup is visible
 * @param {Array} props.suggestions - Array of room member objects to display
 * @param {number} props.selectedIndex - Currently highlighted suggestion index
 * @param {Function} props.onSelect - Callback when a suggestion is selected
 * @param {Function} props.onClose - Callback when the popup should close
 * @param {Object} props.position - Position object { x, y } for popup placement
 */
const MentionAutocomplete = ({
  isOpen,
  suggestions,
  selectedIndex,
  onSelect,
  onClose,
  position,
}) => {
  const { t } = useI18n();
  const menuRef = useRef(null);
  const [adjustedPosition, setAdjustedPosition] = useState(position);

  // Use existing useClickOutside hook for click-outside handling
  useClickOutside(menuRef, onClose, isOpen);

  // Scroll selected item into view
  useEffect(() => {
    if (!isOpen || !menuRef.current) return;

    const selectedItem = menuRef.current.querySelector('.mention-suggestion-item.selected');
    if (selectedItem && typeof selectedItem.scrollIntoView === 'function') {
      selectedItem.scrollIntoView({ block: 'nearest' });
    }
  }, [isOpen, selectedIndex]);

  // Adjust position to prevent viewport overflow
  useLayoutEffect(() => {
    if (!isOpen || !menuRef.current || !position) {
      setAdjustedPosition(position);
      return;
    }

    const menu = menuRef.current;
    const rect = menu.getBoundingClientRect();
    const menuWidth = rect.width || 200; // fallback to min-width
    const menuHeight = rect.height || 280;
    const buffer = 8;

    let newX = position.x;
    let newY = position.y;

    // Check right edge
    if (position.x + menuWidth > window.innerWidth - buffer) {
      newX = window.innerWidth - menuWidth - buffer;
    }

    // Check left edge
    if (newX < buffer) {
      newX = buffer;
    }

    // Check bottom edge - open upward if not enough space
    if (position.y + menuHeight > window.innerHeight - buffer) {
      newY = position.y - menuHeight;
    }

    // Check top edge
    if (newY < buffer) {
      newY = buffer;
    }

    setAdjustedPosition({ x: newX, y: newY });
  }, [isOpen, position]);

  if (!isOpen) return null;

  return (
    <div
      ref={menuRef}
      className="mention-autocomplete"
      style={{
        left: adjustedPosition?.x ?? 0,
        top: adjustedPosition?.y ?? 0,
      }}
      role="listbox"
      aria-label="Mention suggestions"
      aria-expanded={isOpen}
      aria-activedescendant={
        selectedIndex >= 0 && suggestions.length > 0
          ? `mention-suggestion-${selectedIndex}`
          : undefined
      }
    >
      {suggestions.length === 0 ? (
        <div className="mention-autocomplete-empty" role="status" aria-live="polite">
          {t('Mentions.noResults')}
        </div>
      ) : (
        suggestions.map((member, index) => (
          <MentionSuggestionItem
            key={member.userId}
            member={member}
            index={index}
            isSelected={index === selectedIndex}
            onSelect={onSelect}
          />
        ))
      )}
    </div>
  );
};

MentionAutocomplete.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  suggestions: PropTypes.arrayOf(
    PropTypes.shape({
      userId: PropTypes.string.isRequired,
      name: PropTypes.string,
      rawDisplayName: PropTypes.string,
    }),
  ),
  selectedIndex: PropTypes.number,
  onSelect: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  position: PropTypes.shape({
    x: PropTypes.number,
    y: PropTypes.number,
  }),
};

MentionAutocomplete.defaultProps = {
  suggestions: [],
  selectedIndex: 0,
  position: { x: 0, y: 0 },
};

export default MentionAutocomplete;
